import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetSettingsQuery } from 'utils/RTKGraphQL';
import { wrapper } from 'reducers/store';
import { initialState, mapToCamelCase } from 'resources/data/settings';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

// Import actions from the specific slices
import { setUISettings } from 'reducers/slices/uisettings';
import { setLanguageSettings } from 'reducers/slices/languagesettings';
import { setAudioSettings } from 'reducers/slices/audiosettings';
import { setColorSettings } from 'reducers/slices/colorsettings';
import { setQuizSettings } from 'reducers/slices/quizsettings';
import { setSkillSettings } from 'reducers/slices/skillsettings';
import { setDateSettings } from 'reducers/slices/datesettings';
import { saveAll as setCollapseSettings } from 'reducers/slices/collapsesettings';
const withSettingsSync = WrappedComponent => {
  const SettingsSyncComponent = props => {
    const dispatch = useDispatch();
    const {
      store,
      props: componentProps
    } = wrapper.useWrappedStore(props);

    // Get settings from each slice
    const uisettings = useSelector(state => state.uisettings);
    const languagesettings = useSelector(state => state.languagesettings);
    const audiosettings = useSelector(state => state.audiosettings);
    const colorsettings = useSelector(state => state.colorsettings);
    const quizsettings = useSelector(state => state.quizsettings);
    const skillsettings = useSelector(state => state.skillsettings);
    const datesettings = useSelector(state => state.datesettings);
    const collapsesettings = useSelector(state => state.collapsesettings);
    const profile = useSelector(state => state.profile);

    // Combine all settings slices
    const combinedSettings = {
      ...uisettings,
      ...languagesettings,
      ...audiosettings,
      ...colorsettings,
      ...quizsettings,
      ...skillsettings,
      ...datesettings,
      ...collapsesettings
    };
    useEffect(() => {
      if (!profile) return; // Only sync when authenticated

      const updateSettings = () => {
        // Check if settings need to be mapped to camelCase
        const hasCamelCaseKeys = !Object.keys(combinedSettings).some(key => key.includes('_'));
        let updatedSettings = {
          ...combinedSettings
        };

        // Handle skill property specifically
        if (skillsettings.skill && !Array.isArray(skillsettings.skill)) {
          dispatch(setSkillSettings({
            skill: initialState.skill
          }));
        }

        // Map to camelCase if needed
        if (!hasCamelCaseKeys) {
          updatedSettings = mapToCamelCase(updatedSettings);
        }

        // Ensure we have all required settings
        if (Object.keys(combinedSettings).length === 0 || !Array.isArray(skillsettings.skill)) {
          // Initialize missing settings with defaults
          const defaultSettings = {
            ...initialState
          };

          // Update individual slices with their defaults
          if (Object.keys(uisettings).length === 0) {
            const uiDefaults = {
              darkMode: defaultSettings.darkMode,
              showOutline: defaultSettings.showOutline,
              showCharacter: defaultSettings.showCharacter,
              fontFamily: defaultSettings.fontFamily,
              charSize: defaultSettings.charSize,
              pinyinSize: defaultSettings.pinyinSize,
              charSpaceVal: defaultSettings.charSpaceVal
            };
            dispatch(setUISettings(uiDefaults));
          }
          if (Object.keys(languagesettings).length === 0) {
            const languageDefaults = {
              traditional: defaultSettings.traditional,
              showPinyin: defaultSettings.showPinyin,
              showRead: defaultSettings.showRead,
              audioDownload: defaultSettings.audioDownload
            };
            dispatch(setLanguageSettings(languageDefaults));
          }
          if (Object.keys(audiosettings).length === 0) {
            const audioDefaults = {
              speed: defaultSettings.speed,
              volume: defaultSettings.volume
            };
            dispatch(setAudioSettings(audioDefaults));
          }
          if (Object.keys(colorsettings).length === 0) {
            const colorDefaults = {
              charColors: defaultSettings.charColors,
              pinyinColors: defaultSettings.pinyinColors,
              globalDash: defaultSettings.globalDash,
              lightFirstTone: defaultSettings.lightFirstTone,
              lightSecondTone: defaultSettings.lightSecondTone,
              lightThirdTone: defaultSettings.lightThirdTone,
              lightFourthTone: defaultSettings.lightFourthTone,
              lightFifthTone: defaultSettings.lightFifthTone,
              darkFirstTone: defaultSettings.darkFirstTone,
              darkSecondTone: defaultSettings.darkSecondTone,
              darkThirdTone: defaultSettings.darkThirdTone,
              darkFourthTone: defaultSettings.darkFourthTone,
              darkFifthTone: defaultSettings.darkFifthTone
            };
            dispatch(setColorSettings(colorDefaults));
          }
          if (Object.keys(quizsettings).length === 0) {
            const quizDefaults = {
              frontWord: defaultSettings.frontWord,
              frontDefinition: defaultSettings.frontDefinition,
              frontPinyin: defaultSettings.frontPinyin,
              frontAudio: defaultSettings.frontAudio,
              backWord: defaultSettings.backWord,
              backDefinition: defaultSettings.backDefinition,
              backPinyin: defaultSettings.backPinyin,
              backAudio: defaultSettings.backAudio,
              wordSelection: defaultSettings.wordSelection,
              limitSelection: defaultSettings.limitSelection,
              memLimitSelection: defaultSettings.memLimitSelection,
              showHintAfterMiss: defaultSettings.showHintAfterMiss,
              sentenceSelection: defaultSettings.sentenceSelection
            };
            dispatch(setQuizSettings(quizDefaults));
          }
          if (Object.keys(skillsettings).length === 0) {
            const skillDefaults = {
              skill: defaultSettings.skill,
              cats: defaultSettings.cats,
              skillSelected: defaultSettings.skillSelected
            };
            dispatch(setSkillSettings(skillDefaults));
          }
          if (Object.keys(datesettings).length === 0) {
            const dateDefaults = {
              startDate: defaultSettings.startDate,
              endDate: defaultSettings.endDate
            };
            dispatch(setDateSettings(dateDefaults));
          }
          if (Object.keys(collapsesettings).length === 0) {
            const collapseDefaults = {
              dictCollapse: defaultSettings.dictCollapse,
              sentenceCollapse: defaultSettings.sentenceCollapse,
              rulesCollapse: defaultSettings.rulesCollapse
            };
            dispatch(setCollapseSettings(collapseDefaults));
          }
        }
      };

      // Use a debounce to prevent rapid successive updates
      const debouncedUpdate = debounce(updateSettings, 300);
      debouncedUpdate();
      return () => debouncedUpdate.cancel();
    }, [dispatch, profile, uisettings, languagesettings, audiosettings, colorsettings, quizsettings, skillsettings, datesettings, collapsesettings]);
    return /*#__PURE__*/React.createElement(WrappedComponent, componentProps);
  };
  return /*#__PURE__*/React.memo(SettingsSyncComponent, (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  });
};
export default withSettingsSync;